/**
 * 弹层的三件必需品：锁滚动、困住焦点、返回键关闭。
 *
 * 这三件是"弹层看起来像模态框"和"弹层真的是模态框"的差别：
 * - 不锁滚动 → 抽屉打开时背后的列表还在滚，iOS 上尤其明显；
 * - 不困住焦点 → 键盘用户 Tab 两下就跑到背后的页面里去了，读屏用户直接迷路；
 * - 不接管返回 → 安卓用户按返回键直接退出整个应用，而他只是想关掉这个抽屉。
 */
import { useEffect, useRef, useState } from 'react';
import { pushHistoryEntry } from '@pwaship/core/nav-history';
/**
 * 锁住背景滚动。
 *
 * ⚠️ iOS 上 `overflow: hidden` 单独是**不够的**：body 还是会被橡皮筋拖动。
 * 必须用 position: fixed 把它钉住，关闭时再把滚动位置还原——不还原的话
 * 用户关掉抽屉会发现自己回到了列表顶部。
 */
export function useScrollLock(active) {
    useEffect(() => {
        if (!active || typeof document === 'undefined')
            return;
        const body = document.body;
        const scrollY = window.scrollY;
        const prev = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
        };
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        return () => {
            body.style.overflow = prev.overflow;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.width = prev.width;
            window.scrollTo(0, scrollY);
        };
    }, [active]);
}
/**
 * 跟踪 ref 真正指向的 DOM 节点。
 *
 * ⚠️ 存在的理由：**`active` 翻转的那一帧，`ref.current` 常常还是 null。**
 * `Sheet` 的面板要等 `mounted` 状态（在 effect 里设）之后才渲染，
 * `ActionMenu` 要等 `pos` 算出来才渲染。于是 `[active, ref]` 这种依赖写法下，
 * effect 在节点还不存在时跑一次就 early return，而依赖之后再没变过——**它永远不会重跑**。
 *
 * 后果是静默的：抽屉打开了，焦点还留在触发按钮上，读屏用户完全不知道弹出了东西，
 * Tab 也没被困住。（已在真实浏览器复现：打开抽屉后 activeElement 仍在抽屉外。）
 *
 * 这里用一个"每次渲染后核对一次"的 effect 把节点提升成 state，节点一挂上就触发重跑。
 * 有身份比较兜着，不会自激。
 */
export function useRefNode(ref) {
    const [node, setNode] = useState(null);
    useEffect(() => {
        const el = ref.current ?? null;
        setNode((prev) => (prev === el ? prev : el));
    });
    return node;
}
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
/** 把 Tab 焦点困在容器里，并在关闭时还给原来的元素。 */
export function useFocusTrap(active, containerRef) {
    const container = useRefNode(containerRef);
    useEffect(() => {
        if (!active || typeof document === 'undefined')
            return;
        if (!container)
            return;
        const restoreTo = document.activeElement;
        // 打开时把焦点移进来，否则读屏用户完全不知道弹出了东西
        const first = container.querySelector(FOCUSABLE);
        (first ?? container).focus({ preventScroll: true });
        const onKeyDown = (e) => {
            if (e.key !== 'Tab')
                return;
            const items = [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const first = items[0];
            const last = items[items.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            restoreTo?.focus?.({ preventScroll: true });
        };
    }, [active, container]);
}
/**
 * 当前打开着的弹层栈（后进的在末尾）。
 *
 * ⚠️ 为什么需要一个栈：每个 `useEscapeKey` 都把监听挂在 `document` 上，而
 * **`stopPropagation()` 拦不住同一个节点上的其他监听**（那需要 stopImmediatePropagation）。
 * 于是 Sheet 里开一个 ConfirmDialog，一次 Esc 会同时关掉两层。
 *
 * ⚠️ 而且改用 `stopImmediatePropagation()` 方向是**反的**：监听按注册顺序执行，
 * Sheet 先注册所以先跑，它会先关掉自己再把事件掐掉，对话框反而收不到。
 * 所以只能显式记录层级，让**最上面那个**处理，别人一律不响应。
 */
const escapeStack = [];
let escapeSeq = 0;
/** Esc 关闭（桌面）。移动端的返回键由 useBackButton 处理。 */
export function useEscapeKey(active, onClose) {
    const cb = useRef(onClose);
    cb.current = onClose;
    useEffect(() => {
        if (!active || typeof document === 'undefined')
            return;
        escapeSeq += 1;
        const token = { id: escapeSeq };
        escapeStack.push(token);
        const onKeyDown = (e) => {
            if (e.key !== 'Escape')
                return;
            // 只有栈顶那一层响应：嵌套弹层时，一次 Esc 只该关掉最上面那个
            if (escapeStack[escapeStack.length - 1] !== token)
                return;
            e.stopPropagation();
            cb.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            const i = escapeStack.indexOf(token);
            if (i >= 0)
                escapeStack.splice(i, 1);
        };
    }, [active]);
}
/**
 * 接管系统返回键（安卓的返回手势 / 返回键）。
 *
 * 做法是往 history 里压一条状态，popstate 时关闭弹层。
 * 不做这一条，安卓用户按返回键会直接退出整个应用——而他只是想关掉这个抽屉，
 * 这是安卓上对 Web 应用最一致的差评来源。
 *
 * ⚠️ **压栈必须走 `pushHistoryEntry`，不许自己 `pushState`。**
 * 中性化路由（`createNeutralHistory`）靠 `state.p` 还原真实路径，而空 `p` 的安全默认是
 * "落回中性首屏"。自己压一条 `{ myOverlay: 1 }` 的话，用户打开抽屉再按返回键就被踢回首屏了
 * ——弹层关一下，页面没了。`pushHistoryEntry` 会把当前 entry 的 `p` 带过去。
 * 这个约束对图片查看器、筛选面板、任何接管返回键的东西同样成立。
 */
export function useBackButton(active, onClose) {
    const cb = useRef(onClose);
    cb.current = onClose;
    useEffect(() => {
        if (!active || typeof window === 'undefined')
            return;
        const entry = pushHistoryEntry('overlay');
        // ⚠️ **只有"自己那条 entry 被弹掉了"才关闭。**
        // popstate 是全局事件，嵌套弹层时每一层都会收到。而内层弹层被代码关掉时会调
        // `history.back()` 把自己那条 entry 弹掉——那一下的 popstate 会让外层也关掉自己，
        // 表现是"点了抽屉里确认框的取消，整个抽屉跟着没了"。
        //
        // 判据是：弹完之后**我的 entry 还在栈顶**，就说明被弹掉的不是我，我不该动。
        const onPop = () => {
            if (entry.isCurrent())
                return;
            cb.current();
        };
        window.addEventListener('popstate', onPop);
        return () => {
            window.removeEventListener('popstate', onPop);
            // 弹层是被代码关掉的（点了确认/遮罩），那条 history 记录要自己清掉，
            // 不清的话用户之后按返回键会"什么都没发生"一次。
            // dispose 内部会先确认这条 entry 还在栈顶——已经被返回键弹掉时再 back 会多退一步。
            entry.dispose();
        };
    }, [active]);
}
/** 一次装齐四件事。自定义弹层直接用这个，别自己各挑一半。 */
export function useOverlay(options) {
    const { active, onClose, containerRef, lockScroll = true, trapFocus = true, escape = true, backButton = true } = options;
    useScrollLock(active && lockScroll);
    useFocusTrap(active && trapFocus, containerRef);
    useEscapeKey(active && escape, onClose);
    useBackButton(active && backButton, onClose);
}
//# sourceMappingURL=overlay.js.map